/**
 * SEO utility functions and metadata configuration
 * Provides centralized SEO meta tags, Open Graph, and Twitter Card configuration
 */

import { BASE_URL } from './constants'
import { totalModelCount } from '@/features/home/data/homeContent'

// seo.canonical/ogImage are documented as relative paths, but a caller that
// accidentally passes a full URL (e.g. wrong-domain copy-paste) would
// otherwise get silently concatenated into a malformed href.
function resolveUrl(path: string): string {
  return path.startsWith('http') ? path : `${BASE_URL}${path}`
}

/** Strips the trailing " | ModelStack" / " - ModelStack Docs" / etc. brand
 * suffix from a page title, since the OG image already shows the wordmark
 * via the logo — repeating it in the big title text would be redundant. */
function stripBrandSuffix(title: string): string {
  return title.replace(/\s*[|-]\s*ModelStack[^|]*$/i, '').trim() || title
}

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength
    ? `${text.slice(0, maxLength).trimEnd()}...`
    : text
}

/** Small "section" label shown above the title on the OG image, derived
 * from the canonical path so routes don't each need to declare one. */
function eyebrowForPath(path: string | undefined): string | undefined {
  if (!path) return undefined
  const [, first] = path.split('/')
  const labels: Record<string, string> = {
    docs: 'Documentation',
    blogs: 'Blog',
    models: 'AI Model',
    'provider-tiers': 'Provider Tiers',
    agents: 'Agent Teams',
    changelog: 'Changelog',
    status: 'System Status',
    support: 'Support',
    about: 'About',
  }
  return first ? labels[first] : undefined
}

/** Builds the `/og.png?...` URL for the dynamic OG image route (see
 * src/routes/og[.]png.ts) from whatever a page already has: its own
 * title/description and canonical path. Kept relative — generateMetaTags'
 * resolveUrl() prefixes BASE_URL the same way it does for a static image. */
function generateOgImageUrl(seo: SEOConfig): string {
  const params = new URLSearchParams({ title: stripBrandSuffix(seo.title) })
  const subtitle = truncate(seo.description, 140)
  if (subtitle) params.set('subtitle', subtitle)
  const eyebrow = eyebrowForPath(seo.canonical)
  if (eyebrow) params.set('eyebrow', eyebrow)
  return `/og.png?${params.toString()}`
}

export interface SEOConfig {
  title: string
  description: string
  canonical?: string
  ogType?: 'website' | 'article' | 'product'
  ogImage?: string
  ogImageAlt?: string
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  twitterSite?: string
  twitterCreator?: string
  keywords?: Array<string>
  author?: string
  noindex?: boolean
  nofollow?: boolean
}

export const defaultSEO: SEOConfig = {
  title: `ModelStack - Unified AI API Gateway for ${totalModelCount}+ Models`,
  description: `Unrivaled access to Claude, Gemini, GPT, Kimi, QWen, and ${totalModelCount}+ leading AI models via a single, low-latency obsidian gateway.`,
  ogType: 'website',
  ogImage: '/og-image.png',
  ogImageAlt: 'ModelStack - Unified AI API Gateway',
  twitterCard: 'summary_large_image',
  twitterSite: '@ModelStackHQ',
  twitterCreator: '@ModelStackHQ',
  keywords: [
    'AI models',
    'Claude API',
    'GPT API',
    'Gemini API',
    'AI gateway',
    'model aggregation',
    'unified AI API',
  ],
}

export function generateMetaTags(config: Partial<SEOConfig> = {}) {
  const seo = { ...defaultSEO, ...config }
  const canonicalUrl = seo.canonical ? resolveUrl(seo.canonical) : undefined
  // Routes that never customized ogImage (still the literal static default)
  // get a real per-page card generated from their own title/description
  // instead of sharing one generic image. Routes with a genuine custom image
  // (e.g. a blog post's cover image) keep it untouched.
  const effectiveOgImage =
    seo.ogImage === defaultSEO.ogImage ? generateOgImageUrl(seo) : seo.ogImage

  const meta: Array<{
    charSet?: string
    name?: string
    property?: string
    content?: string
    title?: string
  }> = [
    { charSet: 'utf-8' },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1',
    },
    {
      title: seo.title,
    },
    {
      name: 'description',
      content: seo.description,
    },
  ]

  // Keywords
  if (seo.keywords && seo.keywords.length > 0) {
    meta.push({
      name: 'keywords',
      content: seo.keywords.join(', '),
    })
  }

  // Author
  if (seo.author) {
    meta.push({
      name: 'author',
      content: seo.author,
    })
  }

  // Robots
  const robotsDirectives: Array<string> = []
  if (seo.noindex) robotsDirectives.push('noindex')
  if (seo.nofollow) robotsDirectives.push('nofollow')
  if (robotsDirectives.length > 0) {
    meta.push({
      name: 'robots',
      content: robotsDirectives.join(', '),
    })
  }

  // Open Graph tags
  meta.push(
    {
      property: 'og:type',
      content: seo.ogType || 'website',
    },
    {
      property: 'og:title',
      content: seo.title,
    },
    {
      property: 'og:description',
      content: seo.description,
    },
    {
      property: 'og:site_name',
      content: 'ModelStack',
    },
  )

  if (canonicalUrl) {
    meta.push({
      property: 'og:url',
      content: canonicalUrl,
    })
  }

  if (effectiveOgImage) {
    const imageUrl = resolveUrl(effectiveOgImage)
    meta.push({
      property: 'og:image',
      content: imageUrl,
    })
    if (seo.ogImageAlt) {
      meta.push({
        property: 'og:image:alt',
        content: seo.ogImageAlt,
      })
    }
  }

  // Twitter Card tags
  meta.push(
    {
      name: 'twitter:card',
      content: seo.twitterCard || 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: seo.title,
    },
    {
      name: 'twitter:description',
      content: seo.description,
    },
  )

  if (seo.twitterSite) {
    meta.push({
      name: 'twitter:site',
      content: seo.twitterSite,
    })
  }

  if (seo.twitterCreator) {
    meta.push({
      name: 'twitter:creator',
      content: seo.twitterCreator,
    })
  }

  if (effectiveOgImage) {
    const imageUrl = resolveUrl(effectiveOgImage)
    meta.push({
      name: 'twitter:image',
      content: imageUrl,
    })
    if (seo.ogImageAlt) {
      meta.push({
        name: 'twitter:image:alt',
        content: seo.ogImageAlt,
      })
    }
  }

  return meta
}

interface LinkTag {
  rel: string
  href?: string
  crossOrigin?: 'anonymous'
  sizes?: string
  type?: string
  as?: string
}

/**
 * Page-invariant links (favicon, manifest, preconnect, font stylesheets).
 * Rendered ONCE from the root route — do not call this per-route, or every
 * page ends up emitting duplicate copies of these tags alongside root's.
 */
export function generateGlobalLinks(): Array<LinkTag> {
  return [
    // Favicon
    {
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: '/logo.svg',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '192x192',
      href: '/logo192.svg',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '512x512',
      href: '/logo512.svg',
    },
    // Manifest
    {
      rel: 'manifest',
      href: '/manifest.json',
    },
    // Preconnect to external resources
    {
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com',
    },
    {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous' as const,
    },
    {
      rel: 'preconnect',
      href: 'https://www.googletagmanager.com',
    },
    // Preload primes the fetch before the stylesheet link is even parsed,
    // shortening the fetch→parse→fetch-font chain for the icon font.
    {
      rel: 'preload',
      as: 'style',
      href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap',
    },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap',
    },
  ]
}

/**
 * Route-specific links. Currently just the canonical URL — favicon/manifest/
 * preconnect/font links live in `generateGlobalLinks()` (root-only) instead.
 */
export function generateLinks(config: Partial<SEOConfig> = {}): Array<LinkTag> {
  const seo = { ...defaultSEO, ...config }
  const links: Array<LinkTag> = []

  if (seo.canonical) {
    links.push({
      rel: 'canonical',
      href: resolveUrl(seo.canonical),
    })
  }

  return links
}

// Route-specific SEO configurations
export const routeSEO: Record<string, Partial<SEOConfig>> = {
  '/': {
    title: `ModelStack - Unified AI API Gateway for ${totalModelCount}+ Models`,
    description: `Access Claude, Gemini, GPT, Kimi, QWen, and ${totalModelCount}+ leading AI models through a single OpenAI-compatible API. One key, one bill, unlimited possibilities.`,
    canonical: '/',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack - Unified AI Model Gateway',
    keywords: [
      'AI API gateway',
      'unified AI API',
      'Claude API',
      'GPT API',
      'Gemini API',
      'OpenAI compatible',
      'model aggregation',
      'AI proxy',
    ],
  },
  '/models': {
    title: `AI Models - Browse ${totalModelCount}+ Leading Models | ModelStack`,
    description: `Browse and compare ${totalModelCount}+ leading AI models — Claude, GPT, Gemini, Kimi, QWen, and more — with live pricing, context limits, and capabilities for each.`,
    canonical: '/models',
    ogImage: '/og-image.png',
    ogImageAlt: `ModelStack AI Models - ${totalModelCount}+ Leading Models Available`,
    keywords: [
      'AI models',
      'Claude Sonnet',
      'GPT-4.1',
      'Gemini Pro',
      'model comparison',
      'AI pricing',
    ],
  },
  '/about': {
    title: 'About ModelStack - Unified AI API Gateway',
    description: `ModelStack is a unified API gateway giving developers one key, one bill, and one OpenAI-compatible endpoint for Claude, GPT, Gemini, and ${totalModelCount}+ AI models.`,
    canonical: '/about',
    ogImage: '/og-image.png',
    ogImageAlt: 'About ModelStack',
    keywords: [
      'about ModelStack',
      'AI API gateway company',
      'contact ModelStack',
    ],
  },
  '/provider-tiers': {
    title: 'Provider Tiers - Economy, Standard & Premium Routing | ModelStack',
    description:
      "Understand ModelStack's intelligent provider routing that optimizes costs with economy, standard, and premium tiers for each AI model.",
    canonical: '/provider-tiers',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack Provider Tiers',
    keywords: [
      'provider tiers',
      'LLM routing',
      'cost optimization',
      'API proxy',
      'intelligent routing',
    ],
  },
  '/agents': {
    title:
      'Agent Teams - Coordinator, Specialists & Delegate Mode | ModelStack',
    description:
      'Build an agent team on a canvas: one coordinator model, the specialists it can call, and your choice of who writes the reply. Call it with one model id.',
    canonical: '/agents',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack Agent Teams',
    keywords: [
      'agent teams',
      'multi-agent API',
      'coordinator model',
      'delegate mode',
      'model routing',
      'AI agents',
    ],
  },
  '/changelog': {
    title: 'Changelog - Latest Updates & New Features | ModelStack',
    description:
      "Stay updated with ModelStack's latest features, new model additions, API improvements, pricing changes, and platform updates — all in one changelog.",
    canonical: '/changelog',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack Changelog',
    keywords: ['changelog', 'updates', 'new features', 'release notes'],
  },
  '/playground': {
    title: 'AI Playground - Test Claude, GPT & Gemini Models | ModelStack',
    description:
      'Interactive playground to test and compare AI models from Claude, GPT, Gemini and more. Try different prompts and see responses in real-time.',
    canonical: '/playground',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack AI Playground',
    keywords: [
      'AI playground',
      'test AI models',
      'Claude playground',
      'GPT playground',
      'compare models',
    ],
    // The route only gates access with a client-rendered
    // `if (!session) return <Navigate to="/sign-in">` (no beforeLoad/loader
    // redirect), and /sign-in is itself disallowed + noindexed — so an
    // anonymous crawl of this "indexable" URL never actually reaches
    // indexable content.
    noindex: true,
  },
  '/blogs': {
    title: 'Blog - AI Insights, Guides & Updates | ModelStack',
    description:
      'Read the latest articles, tutorials, and insights on AI models, API integration, cost optimization, and best practices from the ModelStack team.',
    canonical: '/blogs',
    ogImage: '/og-image.png',
    ogImageAlt: 'ModelStack Blog',
    keywords: ['AI blog', 'API guides', 'integration tutorials', 'AI insights'],
  },
  '/sign-in': {
    title: 'Sign In - Access Your ModelStack Account',
    description:
      'Sign in to your ModelStack account to access your API keys and manage your usage.',
    canonical: '/sign-in',
    noindex: true,
  },
  '/sign-up': {
    title: `Sign Up - Get Instant Access to ${totalModelCount}+ AI Models | ModelStack`,
    description: `Create your ModelStack account and get instant access to Claude, GPT, Gemini and ${totalModelCount}+ AI models. Start with pay-as-you-go pricing.`,
    canonical: '/sign-up',
    keywords: ['sign up', 'create account', 'AI API access', 'get started'],
  },
  '/dashboard': {
    title: 'Dashboard - Manage Your AI API Account | ModelStack',
    description:
      'Manage your API keys, monitor usage, view analytics, and configure your ModelStack account.',
    canonical: '/dashboard',
    noindex: true,
  },
  '/support': {
    title: 'Support - Get Help via Discord, Email & Telegram | ModelStack',
    description:
      'Get help with ModelStack via Discord, Telegram, or email. Our support team is here to assist with API integration, billing, and technical questions.',
    canonical: '/support',
    keywords: [
      'support',
      'contact',
      'help',
      'customer service',
      'Discord',
      'Telegram',
    ],
  },
  '/privacy': {
    title: 'Privacy Policy - Zero-Log Data Protection | ModelStack',
    description:
      "Learn how ModelStack protects your data with a zero-log policy, what we collect, how it's used, and your privacy rights and choices as a user.",
    canonical: '/privacy',
    keywords: ['privacy policy', 'data protection', 'zero-log'],
  },
  '/terms': {
    title: 'Terms of Service - Usage Guidelines | ModelStack',
    description:
      'Read the terms of service and usage guidelines governing accounts, billing, API usage, and acceptable use of the ModelStack platform.',
    canonical: '/terms',
    keywords: ['terms of service', 'terms and conditions', 'usage policy'],
  },
  '/data-processing': {
    title: 'Data Processing Agreement - GDPR Compliance | ModelStack',
    description:
      "Review ModelStack's Data Processing Agreement covering GDPR compliance, sub-processors, data retention, and how customer data is secured and handled.",
    canonical: '/data-processing',
    keywords: ['data processing agreement', 'DPA', 'GDPR', 'data security'],
  },
  '/status': {
    title: 'System Status - API Uptime & Incidents | ModelStack',
    description:
      'Current status of the ModelStack API gateway, model routing, authentication, dashboard, and billing systems, plus a history of recent incidents.',
    canonical: '/status',
    keywords: ['system status', 'API uptime', 'incident history'],
  },
  '/accept-invitation': {
    title: 'Accept Invitation - ModelStack',
    description: 'Accept your invitation to join a ModelStack workspace.',
    canonical: '/accept-invitation',
    noindex: true,
  },
  '/forgot-password': {
    title: 'Forgot Password - ModelStack',
    description: 'Reset the password for your ModelStack account.',
    canonical: '/forgot-password',
    noindex: true,
  },
  '/reset-password': {
    title: 'Reset Password - ModelStack',
    description: 'Set a new password for your ModelStack account.',
    canonical: '/reset-password',
    noindex: true,
  },
}

/** Head for any 404 (unmatched URL or a route loader's `throw notFound()`),
 * so it doesn't fall back to the homepage's title/description. */
export const notFoundSEO: Partial<SEOConfig> = {
  title: 'Page Not Found - ModelStack',
  description:
    'The page you are looking for does not exist or may have moved. Browse ModelStack models, docs, and guides from the homepage.',
  noindex: true,
}
